/**
 * Approval API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  ApprovalStats,
  ReviewItem,
  ReviewDetail,
  ManagerLeaveRecord,
  ManagerOvertimeRecord,
  ManagerAttendanceRecord,
  ManagerEmployee,
  PaymentDelayItem,
} from '../../types';

const API_PREFIX = '/api/saiens/approval';

/**
 * Get approval stats
 */
export async function getApprovalStats(): Promise<ApiResponse<ApprovalStats>> {
  const response = await apiClient.get<ApiResponse<ApprovalStats>>(
    `${API_PREFIX}/stats`
  );
  return response.data;
}

/**
 * Get pending approvals
 */
export async function getPendingApprovals(): Promise<ApiResponse<ReviewItem[]>> {
  const response = await apiClient.get<ApiResponse<ReviewItem[]>>(
    `${API_PREFIX}/pending`
  );
  return response.data;
}

/**
 * Get approval history
 */
export async function getApprovalHistory(
  params: { offset?: number; limit?: number } = {}
): Promise<ApiResponse<PaginatedData<ReviewItem>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<ReviewItem>>>(
    `${API_PREFIX}/history`,
    { params }
  );
  return response.data;
}

/**
 * Get approval detail
 */
export async function getApprovalDetail(
  id: number
): Promise<ApiResponse<ReviewDetail>> {
  const response = await apiClient.get<ApiResponse<ReviewDetail>>(
    `${API_PREFIX}/${id}`
  );
  return response.data;
}

/**
 * Approve a review
 */
export async function approveReview(
  id: number,
  comment?: string
): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>(
    `${API_PREFIX}/${id}/approve`,
    { comment: comment || '' }
  );
  return response.data;
}

/**
 * Reject a review
 */
export async function rejectReview(
  id: number,
  comment?: string
): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>(
    `${API_PREFIX}/${id}/reject`,
    { comment: comment || '' }
  );
  return response.data;
}

/**
 * Get employee list (manager only)
 */
export async function getManagerEmployees(): Promise<ApiResponse<ManagerEmployee[]>> {
  const response = await apiClient.get<ApiResponse<ManagerEmployee[]>>(
    `${API_PREFIX}/employees`
  );
  return response.data;
}

interface ManagerQueryParams {
  offset?: number;
  limit?: number;
  state?: string;
  employee_id?: number;
  date_from?: string;
  date_to?: string;
}

/**
 * Get all leave records (manager only)
 */
export async function getAllLeaveRecords(
  params: ManagerQueryParams = {}
): Promise<ApiResponse<PaginatedData<ManagerLeaveRecord>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<ManagerLeaveRecord>>>(
    `${API_PREFIX}/leave/all`,
    { params }
  );
  return response.data;
}

/**
 * Get all overtime records (manager only)
 */
export async function getAllOvertimeRecords(
  params: ManagerQueryParams = {}
): Promise<ApiResponse<PaginatedData<ManagerOvertimeRecord>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<ManagerOvertimeRecord>>>(
    `${API_PREFIX}/overtime/all`,
    { params }
  );
  return response.data;
}

/**
 * Get all attendance records (manager only)
 */
export async function getAllAttendanceRecords(
  params: ManagerQueryParams = {}
): Promise<ApiResponse<PaginatedData<ManagerAttendanceRecord>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<ManagerAttendanceRecord>>>(
    `${API_PREFIX}/attendance/all`,
    { params }
  );
  return response.data;
}

/**
 * Get pending payment delay approvals (manager only)
 */
export async function getPaymentDelays(
  params: { offset?: number; limit?: number } = {}
): Promise<ApiResponse<PaginatedData<PaymentDelayItem>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<PaymentDelayItem>>>(
    `${API_PREFIX}/payment-delays`,
    { params }
  );
  return response.data;
}

/**
 * Review a payment delay request (manager only)
 */
export async function reviewPaymentDelay(
  id: number,
  action: 'approve' | 'reject',
  note?: string
): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>(
    `${API_PREFIX}/payment-delays/${id}/review`,
    { action, note: note || '' }
  );
  return response.data;
}
